import { createEffect, createMemo } from 'solid-js';
import { useQuery } from '@tanstack/solid-query';
import { getProfileByName } from '@data-access/coordinates';
import type { ILogger } from '@trimble/logging';
import { csibToCrsUrn, crsUrnFromProfileCsid } from './csib-to-crs-urn';
import { pickFirstLoadedPcGeo } from './geo-reference-merge';
import type { ActiveCrs, PointCloudCrs } from './point-cloud-crs.types';
import type {
  CreatePointCloudCrsStateParams,
  PointCloudCrsState,
} from './point-cloud-crs-state.types';

const DEFAULT_PROFILE_NAME = 'default';

function tryCsibToCrsUrn(
  pointCloudId: string,
  csib64: string,
  logger: ILogger
): string | undefined {
  try {
    return csibToCrsUrn(csib64);
  } catch (error) {
    logger.warn(
      'Failed to derive CRS URN for a point cloud; it has no CRS URN',
      { pointCloudId },
      error instanceof Error ? error : new Error(String(error))
    );
    return undefined;
  }
}

export function createPointCloudCrsState(
  params: CreatePointCloudCrsStateParams
): PointCloudCrsState {
  const {
    loadedCsibs,
    loadedCenters,
    visiblePointCloudIds,
    projectCsib64,
    tenantTrn,
    coordinatesClient,
    queryClient,
    logger,
  } = params;

  // Includes hidden clouds (cleared on unload, not on hide). A CSIB that
  // fails to encode is excluded so it cannot drop the rest of the map.
  const pointCloudCrsById = createMemo<ReadonlyMap<string, PointCloudCrs>>(
    () => {
      const result = new Map<string, PointCloudCrs>();
      for (const [pointCloudId, csib64] of loadedCsibs()) {
        const crsUrn = tryCsibToCrsUrn(pointCloudId, csib64, logger);
        if (crsUrn !== undefined) {
          result.set(pointCloudId, { pointCloudId, csib64, crsUrn });
        }
      }
      return result;
    }
  );

  const activeVisiblePointCloud = createMemo(() =>
    pickFirstLoadedPcGeo(loadedCsibs(), loadedCenters(), visiblePointCloudIds())
  );

  // Only while the project CRS tier is active — not while a point cloud owns it.
  const profileFetchKey = createMemo<string | undefined>(() => {
    if (activeVisiblePointCloud() !== undefined) {
      return undefined;
    }
    if (projectCsib64() === undefined) {
      return undefined;
    }
    return tenantTrn();
  });

  // A failed fetch leaves crsUrn empty; csib64 is still set synchronously below.
  const profileQuery = useQuery(
    () => ({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the client is a stable instance; its baseUrl is the key.
      queryKey: [
        'coordinates',
        'profile',
        coordinatesClient.getConfig().baseUrl,
        profileFetchKey() ?? '',
        DEFAULT_PROFILE_NAME,
      ] as const,
      queryFn: async ({
        queryKey: [, , , trn, profileName],
      }): Promise<string | null> => {
        const response = await getProfileByName({
          client: coordinatesClient,
          path: { tenantTrn: trn, profileName },
        });
        if (response.error) {
          throw new Error(JSON.stringify(response.error));
        }
        return response.data?.csid ?? null;
      },
      enabled: profileFetchKey() !== undefined,
    }),
    () => queryClient
  );

  createEffect(() => {
    if (!profileQuery.isError) {
      return;
    }
    const error = profileQuery.error;
    logger.warn(
      'Failed to load the default coordinate profile; project CRS URN stays empty',
      { tenantTrn: profileFetchKey() },
      error instanceof Error ? error : new Error(String(error))
    );
  });

  const profileCsid = (): string | undefined => profileQuery.data ?? undefined;

  const activeCrs = createMemo<ActiveCrs | undefined>(() => {
    const active = activeVisiblePointCloud();
    if (active !== undefined) {
      return {
        csib64: active.csib,
        crsUrn: pointCloudCrsById().get(active.pointCloudId)?.crsUrn,
        pointCloudId: active.pointCloudId,
      };
    }

    const projectCsib = projectCsib64();
    if (projectCsib === undefined) {
      return undefined;
    }
    const csid = profileCsid();
    return {
      csib64: projectCsib,
      crsUrn: csid !== undefined ? crsUrnFromProfileCsid(csid) : undefined,
      pointCloudId: undefined,
    };
  });

  return { activeCrs, pointCloudCrsById };
}
